import React from 'react';
import { ChevronLeft, ChevronRight, Copy, Ban, CloudRain, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BRAND_COLOUR, SLOT_LABEL } from './useSchedule';

export function BlockChip({ block, project, crew, compact, readOnly, rainRisk, onEdit, onShift, onDuplicate }) {
  const unavailable = block.type === 'unavailable';
  const colour = unavailable ? '#6B7280' : (BRAND_COLOUR[project?.service_type] || '#006400');
  const who = (block.crew_ids || []).map((id) => crew.find((c) => c.id === id)).filter(Boolean);
  const time = block.slot === 'hours' ? `${block.start_time}–${block.end_time}` : block.slot === 'day' ? '' : SLOT_LABEL[block.slot];
  return (
    <div
      className={`group relative rounded-md border-l-4 text-left ${compact ? 'px-1.5 py-1' : 'px-2 py-1.5'} ${unavailable ? 'bg-gray-100 border-gray-400' : rainRisk ? 'bg-sky-50 shadow-sm ring-1 ring-sky-300' : 'bg-white shadow-sm'} ${readOnly ? '' : 'cursor-pointer hover:shadow'}`}
      style={{ borderLeftColor: colour }}
      onClick={() => !readOnly && onEdit(block)}
      data-testid={`block-${block.id}`}
      role="button"
    >
      <div className="flex items-start gap-1 min-w-0">
        {unavailable && <Ban className="w-3 h-3 text-gray-500 mt-0.5 shrink-0" />}
        {rainRisk && <CloudRain className="w-3 h-3 text-sky-600 mt-0.5 shrink-0" title={`${rainRisk.rain_prob}% rain — resin at risk`} data-testid={`block-${block.id}-rain`} />}
        <div className="min-w-0 flex-1">
          <div className={`font-semibold text-gray-900 truncate ${compact ? 'text-[11px]' : 'text-xs'}`}>{unavailable ? block.label : (project?.customer_name || 'Project')}</div>
          <div className={`text-gray-600 truncate ${compact ? 'text-[10px]' : 'text-[11px]'}`}>{unavailable ? '' : block.label}{time ? `${unavailable ? '' : ' · '}${time}` : ''}</div>
          {!compact && block.note && <div className="text-[11px] text-gray-500 truncate italic">{block.note}</div>}
        </div>
      </div>
      {who.length > 0 && (
        <div className="flex gap-0.5 mt-1 flex-wrap">
          {who.map((c) => <span key={c.id} title={c.name} className="text-[9px] font-bold text-white rounded px-1 leading-4" style={{ background: c.colour }}>{compact ? c.name.split(' ')[0].slice(0, 6) : c.name}</span>)}
        </div>
      )}
      {!readOnly && !compact && (
        <div className="absolute top-1 right-1 hidden group-hover:flex gap-0.5 bg-white/95 border rounded-md">
          <button type="button" title="Move back a day" onClick={(e) => { e.stopPropagation(); onShift(block.id, -1); }} className="p-0.5 hover:bg-gray-100 rounded" data-testid={`block-${block.id}-back`}><ChevronLeft className="w-3.5 h-3.5" /></button>
          <button type="button" title="Duplicate to next day" onClick={(e) => { e.stopPropagation(); onDuplicate(block.id, 1); }} className="p-0.5 hover:bg-gray-100 rounded" data-testid={`block-${block.id}-dup`}><Copy className="w-3 h-3" /></button>
          <button type="button" title="Move forward a day" onClick={(e) => { e.stopPropagation(); onShift(block.id, 1); }} className="p-0.5 hover:bg-gray-100 rounded" data-testid={`block-${block.id}-fwd`}><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

export function InstallWindowChip({ project, compact }) {
  const colour = BRAND_COLOUR[project.service_type] || '#006400';
  return (
    <div className={`rounded-md border border-dashed ${compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[11px]'} truncate`}
      style={{ borderColor: colour, color: colour, background: `${colour}0d` }} title={`Install window · ${project.customer_name}`} data-testid={`install-window-${project.id}`}>
      Install · {project.customer_name}
    </div>
  );
}

/** Marketing call (Book My Marketing Call) — confirmed = solid, pending = dashed. Staff click through to the inbox. */
export function MarketingCallChip({ call, compact, isStaff }) {
  const navigate = useNavigate();
  const confirmed = call.status === 'confirmed';
  const who = isStaff ? `${call.name}${call.company ? ` · ${call.company}` : ''}` : 'Ox marketing call';
  return (
    <div
      className={`rounded-md border-l-4 ${confirmed ? 'bg-sky-50 border-sky-600' : 'bg-white border-dashed border border-l-4 border-sky-400'} ${compact ? 'px-1.5 py-0.5' : 'px-2 py-1'} ${isStaff ? 'cursor-pointer hover:shadow' : ''}`}
      style={{ borderLeftColor: '#0369a1' }}
      onClick={() => isStaff && navigate(`/staff/marketing-calls?highlight=${call.id}`)}
      title={`${confirmed ? 'Confirmed' : 'Pending'} marketing call · ${call.time} · ${who}${call.phone ? ` · ${call.phone}` : ''}`}
      data-testid={`marketing-call-${call.id}`}
      role={isStaff ? 'button' : undefined}
    >
      <div className={`flex items-center gap-1 font-semibold text-sky-900 truncate ${compact ? 'text-[11px]' : 'text-xs'}`}>
        <Phone className="w-3 h-3 shrink-0" /> {call.time} {confirmed ? 'Call' : 'Call?'}
      </div>
      {!compact && <div className="text-[11px] text-sky-800/80 truncate">{who}</div>}
    </div>
  );
}
